import { Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import RemoveIcon from '@mui/icons-material/Remove'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { useLayoutEffect, useRef, useState, useTransition } from 'react'
import { ReservableTimes } from '~/lib/feature/reservation'
import { daysOfWeek } from '~/features/campaigns/regularlyPublish/CampaignPublishSchedulePicker'
import { WeekDay } from '~/generated/graphql'
import { colorPalette } from '~/constants/styles/colors'

type Props = {
  schedules: {
    [key: string]: boolean
  }
}

const useLazyTransition = () => {
  const [render, setRender] = useState(false)
  const [isPending, startTransition] = useTransition()

  useLayoutEffect(() => {
    startTransition(() => {
      setRender(true)
    })
  }, [])

  return [render, isPending] as const
}

export const CampaignReservationWeekdayViewer = ({ schedules }: Props) => {
  const [renderFullViewer, isViewerRendering] = useLazyTransition()
  const container = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    if (container.current) {
      container.current.scrollTop = 650 // だいたい9:00の位置にスクロール
    }
  }, [renderFullViewer])

  return (
    <TableContainer
      sx={{
        maxHeight: 600,
        overflowX: 'auto',
      }}
      ref={container}
    >
      <Table stickyHeader aria-busy={isViewerRendering ? true : undefined}>
        <TableHead>
          <TableRow>
            <TableCell
              size="small"
              sx={{
                borderRight: (theme) => '1px solid ' + theme.palette.divider,
                position: 'sticky',
                left: 0,
                top: 0,
                backgroundColor: 'white',
                zIndex: 2,
              }}
            />
            {daysOfWeek.map((day) => (
              <TableCell
                key={day.key}
                size="small"
                sx={{
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  backgroundColor:
                    day.key === WeekDay.Sunday ? '#FFF2F2' : day.key === WeekDay.Saturday ? '#EBF5FF' : 'white',
                  color: day.key === WeekDay.Sunday ? '#A90000' : undefined,
                }}
              >
                {day.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {ReservableTimes.map((time, index) => (
            <TableRow key={time}>
              <TableCell
                size="small"
                sx={{
                  textAlign: 'end',
                  fontWeight: 'bold',
                  border: 'none',
                  borderRight: (theme) => '1px solid ' + theme.palette.divider,
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  backgroundColor: 'white',
                }}
              >
                {time}
              </TableCell>

              {isViewerRendering && index === 0 ? (
                <TableCell
                  colSpan={daysOfWeek.length}
                  rowSpan={ReservableTimes.length}
                  sx={{ position: 'relative', padding: 0 }}
                >
                  <Skeleton variant="rectangular" height="100%" sx={{ position: 'absolute', inset: 0 }} />
                </TableCell>
              ) : undefined}

              {renderFullViewer
                ? daysOfWeek.map((day) => {
                    const key = `${day.key}_${time}`
                    return (
                      <TableCell
                        key={day.key}
                        size="small"
                        sx={{
                          border: 'none',
                          textAlign: 'center',
                        }}
                      >
                        {schedules[key] ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <RemoveIcon
                            sx={{
                              color:
                                day.key === WeekDay.Sunday
                                  ? 'red'
                                  : day.key === WeekDay.Saturday
                                    ? colorPalette.primary.main
                                    : 'text.secondary',
                            }}
                          />
                        )}
                      </TableCell>
                    )
                  })
                : undefined}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
